'use client';
import type { ComponentProps } from 'react';
import type { ScoredStock } from '@/lib/compute';
import type { ProjectionHorizon } from '@/lib/projectScore';
import type { TechnicalStock } from '@/lib/hooks/use-visualizer-data';
import { ScatterPlot } from '@/components/ScatterPlot';
import { DataTable } from '@/components/DataTable';
import { ScatterPlot3D } from '@/components/ScatterPlot3D';
import { BacktestView } from '@/components/BacktestView';
import { StockDetail } from '@/components/StockDetail';
import PillarPerformanceChart from '@/components/PillarPerformanceChart';

type ViewMode = 'heatmap' | 'table' | 'backtest' | 'technical' | '3d';
type SelectedTechnicalData = ComponentProps<typeof StockDetail>['technicalData'];
type BacktestData = ComponentProps<typeof ScatterPlot>['backtestData'];

interface Props {
  viewMode: ViewMode;
  pillarView: string | null;
  onPillarBack: () => void;

  filtered: ScoredStock[];
  filteredHistorical: ScoredStock[] | null;
  stocksToUse: ScoredStock[];

  search: string;
  pinnedSymbols: Set<string>;
  onTogglePin: (sym: string) => void;
  onSelectStock: (sym: string) => void;
  selectedSymbol: string | null;
  selectedStock: ScoredStock | null;
  selectedTechnicalData: SelectedTechnicalData;
  onCloseStockDetail: () => void;

  // 3D-specific
  resetKey: number;
  totalCount: number;
  technicalData: TechnicalStock[];
  technicalWeeklyData: TechnicalStock[];
  technicalMonthlyData: TechnicalStock[];
  factorHighlightStocks: Set<string>;
  sectorTopComposite: Set<string>;
  alphaZoneStocks: Set<string>;
  enteredAlphaSymbols?: Set<string>;
  exitedAlphaSymbols?: Set<string>;
  historicalPeriod: string | null;
  historicalDate: string | null;
  historicalLoading: boolean;
  onHistoricalPeriodChange: (p: string | null) => void;
  updatedAt?: string;
  portfolioIsolated: boolean;
  selectedSectorsCount: number;
  projectionHorizon: ProjectionHorizon;
  onProjectionChange: (h: ProjectionHorizon) => void;
  /** V-axis mode toggle. Passes through to the 3D scatter overlay. */
  vMode?: 'classic' | 'blended';
  onVModeChange?: (m: 'classic' | 'blended') => void;

  // Backtest
  backtestData: BacktestData;
}

/**
 * Right-hand viewport on the home page — picks the active view (3D scatter,
 * 2D heatmap, table, technical scatter, or backtest) based on viewMode, and
 * overlays the StockDetail panel when a symbol is selected.
 *
 * Heavy in props because the home page owns all the underlying state. The
 * goal of this extraction is to keep app/page.tsx focused on state +
 * handlers and isolate the view-mode switch logic here.
 */
export function HomeViewport(p: Props) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {p.pillarView ? (
        <PillarPerformanceChart
          pillar={p.pillarView}
          onBack={p.onPillarBack}
          onStockSelect={p.onSelectStock}
        />
      ) : p.viewMode === 'heatmap' ? (
        <ScatterPlot
          stocks={p.filtered}
          highlightSymbol={p.search}
          pinnedSymbols={p.pinnedSymbols}
          onTogglePin={p.onTogglePin}
          onSelectStock={p.onSelectStock}
          backtestData={p.backtestData}
          portfolioIsolated={p.portfolioIsolated}
          sectorIsolated={p.selectedSectorsCount === 1}
          enteredAlphaSymbols={p.enteredAlphaSymbols}
          exitedAlphaSymbols={p.exitedAlphaSymbols}
          projectionHorizon={p.projectionHorizon}
          onProjectionChange={p.onProjectionChange}
        />
      ) : p.viewMode === 'table' ? (
        <DataTable
          stocks={p.filtered}
          pinnedSymbols={p.pinnedSymbols}
          onTogglePin={p.onTogglePin}
          onSelectStock={p.onSelectStock}
          highlightSymbol={p.search}
          selectedSymbol={p.selectedSymbol}
          technicalData={p.technicalData}
          technicalMonthlyData={p.technicalMonthlyData}
        />
      ) : p.viewMode === 'technical' ? (
        <ScatterPlot
          stocks={p.filtered}
          highlightSymbol={p.search}
          pinnedSymbols={p.pinnedSymbols}
          onTogglePin={p.onTogglePin}
          onSelectStock={p.onSelectStock}
          portfolioIsolated={p.portfolioIsolated}
          sectorIsolated={p.selectedSectorsCount === 1}
          enteredAlphaSymbols={p.enteredAlphaSymbols}
          exitedAlphaSymbols={p.exitedAlphaSymbols}
          projectionHorizon={p.projectionHorizon}
          onProjectionChange={p.onProjectionChange}
          yAxis="momentum"
        />
      ) : p.viewMode === '3d' ? (
        <ScatterPlot3D
          key={p.resetKey}
          stocks={p.filteredHistorical ?? p.filtered}
          totalCount={p.totalCount}
          technicalData={p.technicalData}
          technicalWeeklyData={p.technicalWeeklyData}
          technicalMonthlyData={p.technicalMonthlyData}
          highlightSymbol={p.search}
          onSelectStock={p.onSelectStock}
          selectedSymbol={p.selectedSymbol}
          factorHighlightStocks={p.factorHighlightStocks}
          sectorTopComposite={p.sectorTopComposite}
          alphaZoneStocks={p.alphaZoneStocks}
          enteredAlphaSymbols={p.enteredAlphaSymbols}
          exitedAlphaSymbols={p.exitedAlphaSymbols}
          historicalPeriod={p.historicalPeriod}
          historicalDate={p.historicalDate}
          historicalLoading={p.historicalLoading}
          onHistoricalPeriodChange={p.onHistoricalPeriodChange}
          updatedAt={p.updatedAt}
          portfolioIsolated={p.portfolioIsolated}
          projectionHorizon={p.projectionHorizon}
          onProjectionChange={p.onProjectionChange}
          vMode={p.vMode}
          onVModeChange={p.onVModeChange}
        />
      ) : p.backtestData ? (
        <BacktestView data={p.backtestData} />
      ) : (
        <div className="flex-1 flex items-center justify-center" style={{ background: '#0a0c10' }}>
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-3" />
            <div className="text-[#e2e8f0] text-sm">Loading backtest data...</div>
            <div className="text-white text-xs mt-1">Run <code className="text-blue-400">npm run backtest</code> if not generated yet</div>
          </div>
        </div>
      )}
      {p.selectedStock && (
        <StockDetail
          stock={p.selectedStock}
          allStocks={p.stocksToUse}
          pinnedSymbols={p.pinnedSymbols}
          onTogglePin={p.onTogglePin}
          onClose={p.onCloseStockDetail}
          technicalData={p.selectedTechnicalData}
          noBackdrop={p.viewMode === '3d'}
          isAlphaZone={p.alphaZoneStocks.has(p.selectedStock.symbol)}
        />
      )}
    </div>
  );
}
